import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  OnChanges,
} from '@angular/core';
import { GeSelectPaginationService } from './ge-select-pagination.service';
import { Subject, takeUntil } from 'rxjs';
import { FormControl } from '@angular/forms';

export interface MappedResponse<T> {
  totalElements: number;
  recordsFiltered: number;
  datos: T[];
}

@Component({
  selector: 'lib-ge-select-pagination',
  templateUrl: './ge-select-pagination.component.html',
  styleUrls: ['./ge-select-pagination.component.scss'],
})
export class GeSelectPaginationComponent
  implements OnDestroy, OnInit, OnChanges
{
  constructor(private geSelectPaginationService: GeSelectPaginationService) {}

  @Input() endPointFilter: string = '';
  @Input() translations: string[] = [];
  @Input() descripcion: string | Function = 'descripcion';
  @Input() multiple: boolean = false;
  @Input() disabled: boolean = false;
  @Input() updateList: boolean = false;
  @Input() updateUntilValueFound: boolean = true;
  @Input() customClass: string = '';
  @Input() objQueryName: string | Function = 'descripcion';
  @Input() defaultParameters: any;
  @Input() filterValues: string = '';
  @Input() defaultValue: number | undefined;
  @Input() selectDefaultValue: boolean = false;
  @Input() sortValue: string = '';
  @Output() selectedModel = new EventEmitter<any>();
  @Output() filteredList = new EventEmitter<any>();
  public filteredModelList: any[] = [];
  recordsFiltered: number = 0;
  public totalElements: number = 0;
  pageNumberFilter = 0;
  pageSize = 100;
  onDestroy$: Subject<boolean> = new Subject();
  @Input() modelSelected: any;
  @Input() idSelected: any;
  @ViewChild('selectElement') selectElement: any;
  @ViewChild('box') boxInput: ElementRef | undefined;
  @Input() formControlNameSelect: FormControl = new FormControl();
  @Input() placeHolder: string = '';
  @Input() mapResponseFunction: (reponse: any) => MappedResponse<any> =
    this.defaultMapResponseFunction;
  @Input() anotherTypeSearch: boolean = false;
  @Input() enableTooltip: boolean = false;
  @Input() tooltip: string | ((item: any) => string) | undefined;
  @Input() matTooltipClass: string = 'custom-tooltip';

  input$ = new Subject<string>();
  private v2InitialValueSnapshot: any = null;

  private preserveCurrentValue(): any[] {
    const currentVal = this.formControlNameSelect?.value;
    if (!currentVal) return [];
    return Array.isArray(currentVal) ? [...currentVal] : [currentVal];
  }

  get bindLabelSelect(): string {
    return typeof this.descripcion === 'string' ? this.descripcion : 'descripcion';
  }

  // Comparador opcional para items sin `id` propio (p.ej. CNAE, cuyo id es `clase`).
  @Input() compareWithFn?: (item1: any, item2: any) => boolean;

  compareWith = (item1: any, item2: any) => {
    if (this.compareWithFn) {
      return item1 && item2 ? this.compareWithFn(item1, item2) : item1 === item2;
    }
    return item1 && item2 ? item1.id === item2.id : item1 === item2;
  };

  ngOnInit(): void {
    if (this.formControlNameSelect && this.formControlNameSelect.value) {
      const initialVal = this.formControlNameSelect.value;
      this.filteredModelList = Array.isArray(initialVal) ? [...initialVal] : [initialVal];
    }

    if (!this.idSelected) {
      this.getNextBatchFiltered();
    }
  }

  async ngOnChanges(changes: any) {
    if (changes.idSelected && (changes.idSelected.currentValue || changes.idSelected.currentValue === 0)) {
      this.pageNumberFilter = 0;
      this.filteredModelList = this.preserveCurrentValue();
      const maxTries = 10;
      let result = false;
      const targetId = changes.idSelected.currentValue;
      
      for (let i = 0; i < maxTries && !result; i++) {
        result = this.filteredModelList.find(
          (elem) => elem.id == targetId
        );
        if (!result) {
          await this.callSearch('');
          // If after callSearch we still don't have it, and totalElements is less than what we've fetched, break
          if (this.filteredModelList.length >= this.totalElements && this.totalElements > 0) {
              const checkAgain = this.filteredModelList.find(elem => elem.id == targetId);
              if (!checkAgain) break;
          }
        } else {
          this.modelSelected = result;
          // Actualizamos el objeto en el formGroup padre
          this.formControlNameSelect.setValue(result, { emitEvent: true });
          // Forzamos actualización visual del ng-select si es necesario
          if (this.selectElement) {
              this.selectElement.select(result);
          }
          // Si ya lo tenemos de preserveCurrentValue, cargamos la primera página de todas formas para que el combo no esté vacío
          if (this.pageNumberFilter === 0) {
             await this.callSearch('');
          }
        }
      }
    }
    if (changes.updateList && !changes.updateList.firstChange) {
      this.filteredModelList = this.preserveCurrentValue();
      this.pageNumberFilter = 0;
      if (this.boxInput?.nativeElement?.value) {
        this.boxInput.nativeElement.value = '';
      }
      await this.callSearch('');
    }

    if (changes.modelSelected && changes.modelSelected.currentValue) {
      if (this.multiple) {
        this.filterData(changes.modelSelected.currentValue);
      }
    }
    if (
      changes.modelSelected &&
      changes.modelSelected.currentValue == undefined &&
      changes.modelSelected.firstChange == false
    ) {
      if (changes.modelSelected.previousValue != undefined) {
        if (this.multiple) {
          this.filterData(changes.modelSelected.previousValue);
        } else {
          if (Array.isArray(changes.modelSelected.previousValue) == false) {
            this.filterDataSingleModel(changes.modelSelected.previousValue);
          }
        }
      }
    }
  }

  filterDataSingleModel(previousValue: any) {
    if (previousValue.length > 0) {
      const result = this.filteredModelList.find(
        (elem) => this.getItemDescription(elem) == previousValue
      );

      if (result == undefined && this.totalElements >= this.pageSize) {
        this.findElementByModel('', previousValue);
      } else {
        this.modelSelected = result;
      }
    }
  }

  filterData(data: any) {
    if (data.datos.length > 0) {
      const result = this.filteredModelList.filter((item2) =>
        this.modelSelected.some((item1: any) => {
          return (
            this.getItemDescription(item1) == this.getItemDescription(item2)
          );
        })
      );
      if (result.length == 0 && this.totalElements >= this.pageSize) {
        this.findElementByModel('', data);
      } else {
        this.modelSelected = result;
      }
    } else {
    }
  }

  findElementByModel(searchTerm: any, data: any) {
    this.geSelectPaginationService
      .getSearchResultLibrary(
        this.endPointFilter,
        this.objQueryName,
        searchTerm,
        this.pageNumberFilter,
        this.pageSize,
        this.sortValue,
        this.anotherTypeSearch,
        this.defaultParameters
      )
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((response: any) => {
        const mappedRes = this.mapResponseFunction(response);
        this.totalElements = mappedRes.totalElements;
        this.recordsFiltered = mappedRes.recordsFiltered;
        this.filteredModelList.push(
          ...mappedRes.datos.map((modelData: any) => {
            return modelData;
          })
        );
        this.filteredList.emit(this.filteredModelList);
        this.pageNumberFilter += 1;
        this.filterData(data);
      });
  }

  async getNextBatchFiltered() {
    const input = this.boxInput?.nativeElement?.value
      ? this.boxInput?.nativeElement?.value
      : '';
    await this.callSearch(input);
  }

  change(index: number) {
    this.selectedModel.emit(this.modelSelected);
  }

  getItemDescription(item: any) {
    if (!item) return '';
    if (typeof item === 'string') return item;
    if (typeof this.descripcion == 'function') {
      return this.descripcion(item);
    }
    return item[this.descripcion as string];
  }

  computeTooltip(item: any): string {
    if (!this.enableTooltip) return '';
    try {
      if (this.tooltip) {
        if (typeof this.tooltip === 'function') {
          return (this.tooltip as any)(item) || '';
        } else if (typeof this.tooltip === 'string') {
          return item[this.tooltip] != null
            ? item[this.tooltip].toString()
            : (this.tooltip as string);
        }
      }
      const desc = this.getItemDescription(item);
      return desc != null ? desc.toString() : '';
    } catch (error) {
      return '';
    }
  }


  callSearch(searchTerm: string) {
    const term = searchTerm || '';
    if (this.filterValues && !searchTerm) {
      searchTerm = this.filterValues;
    }
    return new Promise((resolve, reject) => {
      this.geSelectPaginationService
        .getSearchResultLibrary(
          this.endPointFilter,
          this.objQueryName,
          term,
          this.pageNumberFilter,
          this.pageSize,
          this.sortValue,
          this.anotherTypeSearch,
          this.defaultParameters
        )
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(
          (response) => {
            const mappedRes = this.mapResponseFunction(response);
            this.totalElements = mappedRes.totalElements;
            this.recordsFiltered = mappedRes.recordsFiltered;
            // FIX: Obtenemos los IDs que ya hemos insertado manualmente para no duplicarlos
            // Filtramos la respuesta del backend
            const newItems = mappedRes.datos.filter(
              (modelData: any) => !this.filteredModelList.some((item: any) => this.compareWith(item, modelData))
            );
            
            // Insertamos los datos limpios
            this.filteredModelList.push(...newItems);
            if (this.defaultValue != undefined) {
              this.filteredModelList.sort((a, b) => {
                if (a.id === this.defaultValue) {
                  return -1;
                } else if (b.id === this.defaultValue) {
                  return 1;
                }
                return a;
              });
              if(this.selectDefaultValue){
                this.selectDefaultVal();
              }
            }
            this.filteredList.emit(this.filteredModelList);
            this.pageNumberFilter += 1;
            setTimeout(() => {
              const valueToRegister = this.v2InitialValueSnapshot || this.formControlNameSelect?.value;

              if (valueToRegister) {
                this.formControlNameSelect.setValue(valueToRegister, { emitEvent: false });
                this.v2InitialValueSnapshot = null;
              }
            }, 0);
            resolve(this.filteredModelList);
          },
          (error) => {
            reject(error);
          }
        );
    });
  }

  async filterSearch(searchTerm: string) {
    if (!searchTerm) {
      return;
    } else {
      searchTerm = searchTerm.toLowerCase();
      this.filteredModelList = [];
      this.pageNumberFilter = 0;
      await this.callSearch(searchTerm);
    }
  }
  async clearFilter() {
    this.filteredModelList = this.preserveCurrentValue();
    this.pageNumberFilter = 0;
    if (this.boxInput?.nativeElement?.value) {
      this.boxInput.nativeElement.value = '';
    }
    await this.callSearch('');
  }

  defaultMapResponseFunction<T>(response: any): MappedResponse<T> {
    const mappedResponse: MappedResponse<T> = {
      totalElements: 0,
      recordsFiltered: 0,
      datos: [],
    };

    // Comprobamos si 'response.content' existe y tiene datos
    if (response.content && Array.isArray(response.content)) {
      mappedResponse.datos = response.content;
      mappedResponse.totalElements = response.totalElements;
    } else {
      mappedResponse.datos = response.datos;
      mappedResponse.totalElements = response.paginacion?.totalElements;
      mappedResponse.recordsFiltered = response.paginacion?.recordsFiltered;
    }

    return mappedResponse;
  }
  selectDefaultVal(): void {
    if (
      this.defaultValue !== undefined &&
      this.formControlNameSelect.value === null
    ) {
      const defaultItem = this.filteredModelList.find(
        (item) => item.id === this.defaultValue
      );
      if (defaultItem) {
        this.modelSelected = defaultItem;
        this.formControlNameSelect.setValue(
          this.getItemDescription(defaultItem)
        );
        this.selectedModel.emit(this.modelSelected);
      } else {
        this.findElementByModel('', this.defaultValue);
      }
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  resetPaginationAndClearInput() {
    this.filteredModelList = [];
    
    // FIX: Preservar el valor actual para que ng-select no lo borre al vaciar las opciones
    const currentValue = this.formControlNameSelect?.value;
    if (currentValue) {
      if (this.multiple && Array.isArray(currentValue)) {
        this.filteredModelList.push(...currentValue);
      } else if (!this.multiple && !Array.isArray(currentValue)) {
        this.filteredModelList.push(currentValue);
      }
    }

    this.pageNumberFilter = 0;
    if (this.boxInput?.nativeElement?.value) {
      this.boxInput.nativeElement.value = '';
    }
  }

  getBindLabel(): string {
    return typeof this.descripcion === 'string' ? this.descripcion : '';
  }
  compareById(item: any, selected: any): boolean {
    return item && selected ? item.id === selected.id : item === selected;
  }
}
